import "./map-selection-page.css";
import { escapeHtml } from "./escapeHtml.js";

export interface MapSelectionEntry {
  id: string;
  name: string;
  status: "playable" | "preview";
  unlocked: boolean;
  lockReason: string | null;
}

export interface MapSelectionPageOptions {
  maps: readonly MapSelectionEntry[];
  defaultMapId: string;
  activeSquadCount: number;
  externalCapacity: number;
  ninjaCount: number;
  rosterCapacity: number;
  onGameModeSelect: (
    mode: "pve" | "pvp",
    mapId: string,
    trigger: HTMLButtonElement,
  ) => void;
}

interface MapPreviewAsset {
  src: string;
}

const MAP_PREVIEWS: Readonly<Record<string, MapPreviewAsset>> = {
  outer_forest: {
    src: "/assets/maps/outer-forest.webp",
  },
  frontier_canyon: {
    src: "/assets/maps/frontier-canyon.webp",
  },
  black_forge_range: {
    src: "/assets/maps/black-forge-range.webp",
  },
};

export function mountMapSelectionPage(
  root: HTMLElement,
  options: MapSelectionPageOptions,
): void {
  const availableMaps = options.maps.filter((map) => map.unlocked);
  let selectedMap =
    availableMaps.find((map) => map.id === options.defaultMapId) ??
    availableMaps[0] ??
    null;

  root.innerHTML = `
    <div class="map-selection-page">
      <section class="deployment-summary" aria-label="Squad summary">
        <div class="deployment-summary-item"><span>ACTIVE SQUAD</span><strong>${options.activeSquadCount} <small>/ ${options.externalCapacity}</small></strong></div>
        <span class="deployment-summary-divider" aria-hidden="true"></span>
        <div class="deployment-summary-item"><span>ROSTER</span><strong>${options.ninjaCount} <small>/ ${options.rosterCapacity}</small></strong></div>
      </section>
      <section class="map-selection-section" aria-labelledby="pve-maps-heading">
        <div class="map-selection-section-heading">
          <div><p class="eyebrow">VILLAGE OPERATIONS</p></div>
          <span class="map-selection-count">${options.maps.length} MAPS</span>
        </div>
        <div class="map-selection-action">
          <div class="map-selection-current" aria-live="polite">
            <span>SELECTED DESTINATION</span>
            <strong id="selected-map-name">${selectedMap ? escapeHtml(displayMapName(selectedMap.name)) : "Choose an available region"}</strong>
          </div>
          <button id="enter-selected-map" class="button button-primary" type="button"${selectedMap ? ` data-map-id="${escapeHtml(selectedMap.id)}"` : " disabled"}>
            <span>${selectedMap ? `Enter ${escapeHtml(displayMapName(selectedMap.name))}` : "Choose a region"}</span><span aria-hidden="true">↗</span>
          </button>
        </div>
        <div class="map-selection-grid">
          ${options.maps.length ? options.maps.map(renderMapCard).join("") : '<p class="map-selection-empty">No regions are available right now.</p>'}
        </div>
      </section>

      <section class="pvp-operation" aria-labelledby="pvp-operation-heading">
        <img class="pvp-operation-image" src="/assets/maps/outer-forest.webp" alt="" width="960" height="540" loading="lazy" decoding="async" />
        <div class="pvp-operation-copy">
          <p class="eyebrow">COMPETITIVE OPERATIONS <span class="pvp-experimental">EXPERIMENTAL</span></p>
          <h2 id="pvp-operation-heading">PvP Arena</h2>
          <p>Enter the PvP queue and take your squad into the arena.</p>
        </div>
        <button id="enter-pvp-arena" class="button button-outline" type="button">Join PvP queue <span aria-hidden="true">↗</span></button>
      </section>

      <p id="notice" class="notice" role="status"></p>
    </div>`;

  const cards = root.querySelectorAll<HTMLButtonElement>("[data-map-choice]");
  cards.forEach((card) => {
    card.addEventListener("click", () => {
      const selectedId = card.dataset.mapChoice;
      selectedMap =
        options.maps.find((map) => map.id === selectedId && map.unlocked) ??
        null;
      updateSelectedMap(root, cards, selectedMap);
    });
  });

  const enterMapButton = root.querySelector<HTMLButtonElement>(
    "#enter-selected-map",
  );
  const enterPvpButton =
    root.querySelector<HTMLButtonElement>("#enter-pvp-arena");
  if (!enterMapButton || !enterPvpButton)
    throw new Error("Map selection actions failed to mount");

  enterMapButton.addEventListener("click", () => {
    if (selectedMap)
      options.onGameModeSelect("pve", selectedMap.id, enterMapButton);
  });
  enterPvpButton.addEventListener("click", () =>
    options.onGameModeSelect("pvp", options.defaultMapId, enterPvpButton),
  );

  updateSelectedMap(root, cards, selectedMap);
}

function renderMapCard(map: MapSelectionEntry, index: number): string {
  const preview = MAP_PREVIEWS[map.id];
  const isPreview = map.status === "preview";
  const name = displayMapName(map.name);
  const lockReason = map.unlocked
    ? ""
    : `<p class="map-card-lock-reason">${escapeHtml(map.lockReason ?? "This region is locked.")}</p>`;
  const status = map.unlocked
    ? isPreview
      ? '<span class="map-card-badge preview">PREVIEW</span>'
      : '<span class="map-card-badge available">AVAILABLE</span>'
    : '<span class="map-card-badge locked">LOCKED</span>';
  const previewBadge =
    !map.unlocked && isPreview
      ? '<span class="map-card-badge preview">PREVIEW</span>'
      : "";
  const image = preview
    ? `<img src="${preview.src}" alt="" width="960" height="540" loading="${index === 0 ? "eager" : "lazy"}" decoding="async"${index === 0 ? ' fetchpriority="high"' : ""} />`
    : '<span class="map-card-image-fallback" aria-hidden="true">&#x2726;</span>';

  return `<button class="map-choice-card${map.unlocked ? "" : " is-locked"}" type="button" data-map-choice="${escapeHtml(map.id)}" aria-pressed="false"${map.unlocked ? "" : " disabled"}${map.lockReason ? ` title="${escapeHtml(map.lockReason)}"` : ""}>
    <span class="map-card-art">${image}<span class="map-card-art-shade" aria-hidden="true"></span></span>
    <span class="map-card-copy"><span class="map-card-title-row"><span class="map-card-name">${escapeHtml(name)}</span><span class="map-card-badges">${status}${previewBadge}</span></span>${lockReason}</span>
  </button>`;
}

function updateSelectedMap(
  root: HTMLElement,
  cards: NodeListOf<HTMLButtonElement>,
  selectedMap: MapSelectionEntry | null,
): void {
  cards.forEach((card) => {
    const selected = card.dataset.mapChoice === selectedMap?.id;
    card.classList.toggle("is-selected", selected);
    card.setAttribute("aria-pressed", String(selected));
  });

  const selectedName = root.querySelector<HTMLElement>("#selected-map-name");
  const enterMapButton = root.querySelector<HTMLButtonElement>(
    "#enter-selected-map",
  );
  if (!selectedName || !enterMapButton) return;

  selectedName.textContent = selectedMap
    ? displayMapName(selectedMap.name)
    : "Choose an available region";
  enterMapButton.disabled = !selectedMap;
  if (selectedMap) enterMapButton.dataset.mapId = selectedMap.id;
  else delete enterMapButton.dataset.mapId;

  const label = enterMapButton.querySelector<HTMLElement>("span");
  if (label)
    label.textContent = selectedMap
      ? `Enter ${displayMapName(selectedMap.name)}`
      : "Choose a region";
}

function displayMapName(name: string): string {
  return name
    .replace(/\s+[\u00b7\u2022\u2013\u2014-]\s+.+$/u, "")
    .replace(/\s+\d+\s*\/\s*\d+\s*$/u, "")
    .trim();
}
